/**
 * UI-only gating of cloud provider types.
 *
 * Which provider types the UI exposes for selection/creation is controlled by the
 * REACT_APP_UI_SUPPORTED_PROVIDERS build-time env var: a comma-separated list of
 * provider types, e.g. "aws,azure". This is a *UI-only* restriction — the backend
 * still knows about every provider type; we simply hide the disabled ones from
 * operations filters and provider-creation forms.
 *
 *   Dev:    set REACT_APP_UI_SUPPORTED_PROVIDERS in frontend/.env
 *   Deploy: set the GitHub Actions repository variable UI_SUPPORTED_PROVIDERS; the
 *           frontend-cicd workflow maps it to REACT_APP_UI_SUPPORTED_PROVIDERS at
 *           build time (CRA only inlines env vars prefixed with REACT_APP_).
 *
 * When the var is unset or empty, every known provider type is supported (no
 * gating), so the default behaviour is unchanged.
 */

// Every cloud provider type the system knows about. Keep in sync with the
// `provider_type` union in ../types/providers.ts.
export const ALL_PROVIDER_TYPES = ['aws', 'gcp', 'azure', 'openstack'] as const;

// `null` means no restriction was configured; otherwise the allow-list of types.
const UI_SUPPORTED_PROVIDERS: string[] | null = (() => {
  const raw = process.env.REACT_APP_UI_SUPPORTED_PROVIDERS;
  if (!raw) return null;
  const list = raw
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  return list.length > 0 ? list : null;
})();

/** True if the given provider type should be selectable/creatable in the UI. */
export function isProviderUiSupported(providerType: string | null | undefined): boolean {
  if (!providerType) return false;
  if (!UI_SUPPORTED_PROVIDERS) return true; // not configured → all enabled
  return UI_SUPPORTED_PROVIDERS.includes(providerType.toLowerCase());
}

/** The provider types the UI exposes, in canonical order (respects the gating var). */
export function getUiSupportedProviderTypes(): string[] {
  return ALL_PROVIDER_TYPES.filter((t) => isProviderUiSupported(t));
}

/** Filter a list down to UI-supported provider types via a type accessor. */
export function filterUiSupportedProviders<T>(
  items: T[],
  getType: (item: T) => string | null | undefined
): T[] {
  return items.filter((item) => isProviderUiSupported(getType(item)));
}
